import { getRequest, deleteRequest, postRequest, putRequest } from './rest'
import { Device } from 'lib/types'
import {
  ALL_ORGS_SELECTED,
  API_ALERT_SOURCES,
  API_DEFAULT_LIMIT,
  API_MICRO_JWT,
  API_UNREGISTER_ALERT_SOURCE,
  API_V5_ALERT_SOURCES,
} from 'lib/constants'
import { decodeJsonApiObject, getObjectsCSV } from './ApiLib'
import { Pagination, decodePagination, EMPTY_PAGINATION } from './pagination'

export interface AlertSourcesWithPagination {
  alertSources: Device[]
  pagination: Pagination
}

export interface AlertSourceParams extends Record<string, any> {
  disabled?: boolean
  firmwareStatus?: boolean
  responder?: boolean
  org_id?: string
  afterCursor?: string | null
  beforeCursor?: string | null
  limit?: number | string
}

const DEFAULT_ALERT_SOURCE_PARAMS: AlertSourceParams = {
  disabled: false,
  firmwareStatus: true,
  limit: API_DEFAULT_LIMIT,
}

export const EMPTY_ALERT_SOURCES: AlertSourcesWithPagination = {
  alertSources: [],
  pagination: EMPTY_PAGINATION,
}

export function createMicroJWT(responder: boolean, org_id: string): Promise<Device> {
  return postRequest({
    endpoint: `${API_MICRO_JWT}`,
    data: { responder: responder },
    header: { org_id },
  }).then(({ data }) => decodeJsonApiObject<Device>(data.data))
}

export function getAlertSourceById(id: string): Promise<Device> {
  // console.log('!!reloading device - main function')
  return getRequest({
    endpoint: `${API_ALERT_SOURCES}/${id}`,
  }).then(({ data }) => {
    return { ...decodeJsonApiObject(data.data), lastFetched: new Date().getTime() }
  })
}

export const getAlertSourcesRaw = (params: AlertSourceParams = {}): Promise<Device[]> => {
  const qstr = new URLSearchParams(DEFAULT_ALERT_SOURCE_PARAMS)
  Object.entries(params).forEach(([key, value]: [key: string, value: any]) => {
    if (value) qstr.set(key, value.toString())
  })
  return getRequest({
    endpoint: `${API_ALERT_SOURCES}?${qstr.toString()}`,
    header: { org_id: params.orgId },
  }).then(({ data }) => data)
}

export const getAlertSourcePage = (params: AlertSourceParams = {}): AlertSourcesWithPagination => {
  // @ts-ignore
  return (
    getAlertSourcesRaw(params)
      // @ts-ignore
      .then(({ data, meta }) => {
        const pagination = decodePagination(meta, data)
        return {
          alertSources: data.map((d: any) => decodeJsonApiObject<Device>(d)),
          pagination,
        }
      })
  )
}

export const getAllAlertSourcesWithPagination = (
  params: AlertSourceParams,
  alertSources: AlertSourcesWithPagination = { alertSources: [], pagination: EMPTY_PAGINATION },
): AlertSourcesWithPagination => {
  // @ts-ignore
  return (
    getAlertSourcesRaw(params)
      // @ts-ignore
      .then(({ data, meta }) => {
        const items = alertSources.alertSources.concat(data)
        const pagination = decodePagination(meta, data)
        if (pagination.after) {
          return getAllAlertSourcesWithPagination(
            { ...params, afterCursor: pagination.after },
            { alertSources: items, pagination },
          )
        } else {
          return {
            alertSources: items.map((d: any) => decodeJsonApiObject<Device>(d)),
            pagination,
          }
        }
      })
  )
}

export const deleteAlertSource = (deviceId: string) =>
  deleteRequest({
    endpoint: `${API_ALERT_SOURCES}/${deviceId}`,
  })

export const updateAlertSource = (deviceWithId: Device): Promise<Device> => {
  const { id, ...alertSource } = deviceWithId
  return putRequest({
    endpoint: `${API_ALERT_SOURCES}/${id}`,
    data: { alertSource },
  }).then(({ data }) => decodeJsonApiObject<Device>(data.data))
}

export const updateAlertSourcePartial = (id: string, alertSource: Partial<Device>): Promise<Device> =>
  putRequest({
    endpoint: `${API_ALERT_SOURCES}/${id}`,
    data: { alertSource },
  }).then(({ data }) => decodeJsonApiObject<Device>(data.data))

export const unregisterAlertSource = (id: string, orgId: string): Promise<[]> =>
  putRequest({
    endpoint: `${API_UNREGISTER_ALERT_SOURCE}/${id}`,
    header: { org_id: orgId },
  }).then(({ data }) => data.data)

export const disableAlertSource = (id: string): Promise<[]> =>
  putRequest({
    endpoint: `${API_ALERT_SOURCES}/${id}`,
    data: { alertSource: { disabled: true } },
  }).then(({ data }) => data.data)

export const getDevicesCSV = (orgId: string): Promise<void> => {
  return getObjectsCSV('alert_sources', orgId)
}

export const getV5AlertSourcesCSV = (orgId: string): Promise<void> => {
  return getRequest({
    endpoint: `${API_V5_ALERT_SOURCES}/download`,
    header: orgId === ALL_ORGS_SELECTED ? {} : { org_id: orgId },
  })
    .then((response: any) => {
      const csvData: string = response.data

      const blob = new Blob([csvData], { type: 'text/csv' })

      const link = document.createElement('a')
      link.download = `alertSources.csv`
      link.href = window.URL.createObjectURL(blob)

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    })
    .catch((error: Error) => {
      console.error('Error fetching CSV data:', error)
      throw error
    })
}

// ── Essential-only list ────────────────────────────────────────────────────

const essentialOnlyEndpoint = (alertSourceId: string) =>
  `${API_V5_ALERT_SOURCES}/${alertSourceId}/essential_only`

/**
 * Every voice button in `orgId`, excluded or not.
 *
 * The v5 filter accepts one field at a time through `getRequestV5`, so the
 * whole set is pulled once and split on `essentialOnly` by the caller rather
 * than fetched twice. Pages at 50; the loop follows `meta.totalCount` the same
 * way `getGroups` does.
 */
export const getVoiceButtons = async (orgId: string): Promise<Device[]> => {
  const all: Device[] = []
  let page = 1

  // safety cap on iterations — 50 pages * 50 per page = 2,500 buttons max
  while (page <= 50) {
    const res: any = await getRequest({
      endpoint: `${API_V5_ALERT_SOURCES}?filter[deviceType]=voice_button&page=${page}`,
      header: orgId === ALL_ORGS_SELECTED ? {} : { org_id: orgId },
    })
    const items: Device[] = res?.data?.data || []
    const totalCount: number = res?.data?.meta?.totalCount ?? all.length + items.length

    all.push(...items)
    if (items.length === 0 || all.length >= totalCount) break
    page += 1
  }

  return all
}

/** Excludes a voice button from voice. Revokes its license server-side. */
export const addEssentialOnly = (orgId: string, alertSourceId: string): Promise<Device> =>
  putRequest({
    endpoint: essentialOnlyEndpoint(alertSourceId),
    header: { org_id: orgId },
  }).then(({ data }: any) => data?.data ?? data)

/** Returns a voice button to voice. Re-provisions its license server-side. */
export const removeEssentialOnly = (orgId: string, alertSourceId: string): Promise<Device> =>
  deleteRequest({
    endpoint: essentialOnlyEndpoint(alertSourceId),
    header: { org_id: orgId },
  }).then(({ data }: any) => data?.data ?? data)
